/**
 * Hard caps applied to every inbound payload before it reaches the engine.
 *
 * These are not style preferences. `matchLoop` runs shared state: an unhandled
 * exception there does not fail one request, it can take down the match for
 * everyone in the room. Rejecting oversized and malformed input early is the
 * cheapest protection we have, and it is why these validators are hand-written
 * rather than delegated (see README).
 *
 * The socket itself is capped at 8KB by docker/nakama/local.yml. These sit
 * comfortably inside that so a rejection is a clean ERROR, not a dropped
 * connection.
 */
export const Limits = {
    /** Longest accepted move name, e.g. "playCard". */
    MOVE_NAME: 64,
    /** Longest accepted host action name. */
    ACTION_NAME: 32,
    /**
     * Longest typed chat message, measured AFTER normalising.
     *
     * A line, not a paragraph. Measured after control characters are stripped and
     * whitespace runs collapsed, because otherwise a hundred newlines is a legal
     * message that draws as a blank bubble.
     */
    CHAT_TEXT: 140,
    /**
     * Longest signalling blob.
     *
     * An audio-only SDP offer is one to three kilobytes and an ICE candidate is a
     * line, so this is generous for both.
     *
     * **It has to stay well inside Nakama's own socket limit**, which is 8192
     * bytes for the whole websocket message — envelope, match id and all. That
     * is not a rejection you can catch: measured on a real stack, a payload of
     * 5000 arrives, 6000 does not, and the socket is **dropped** rather than the
     * message refused. A player sending a large offer would simply fall out of
     * the match, looking like a random disconnection.
     *
     * So the cap is set low enough that this validator always speaks first, and
     * a client that sends too much is told why instead of vanishing.
     */
    VOICE_PAYLOAD: 4096,
    /** Most arguments a single move may carry. */
    MOVE_ARGS: 16,
    /** Longest accepted string anywhere inside a move argument, e.g. a card ID. */
    STRING: 128,
    /** Most entries in any array nested inside a move argument. */
    ARRAY: 64,
    /** Most keys in any object nested inside a move argument. */
    OBJECT_KEYS: 32,
    /** How deep an argument may nest. Guards against stack exhaustion on parse. */
    DEPTH: 6,
};
/**
 * Keys refused anywhere in an inbound payload.
 *
 * Move arguments are client-supplied strings that end up as lookup keys. On a
 * plain object those three keys reach the prototype chain, which is a pollution
 * vector; refusing them outright is simpler than auditing every consumer.
 */
export const FORBIDDEN_KEYS = ['__proto__', 'constructor', 'prototype'];
//# sourceMappingURL=limits.js.map